import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  CloseButton,
  Form,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import {
  DocumentSubType,
  type MiscContent,
  MiscContentType,
  Role,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import CustomEditor from "@/components/Editor/CustomEditor";
import LeavingGuard from "@/components/LeavingGuard";
import { FormModal } from "@/components/Modals";
import PlaceholderTutorialLink from "@/components/PlaceholderTutorialLink";
import { useFirmApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import styles from "./PreliminaryStatement.module.scss";
import type {
  LibraryMiscContentOnUpdate,
  PreliminaryStatementPreferencesWithDocument,
} from "./types";
import {
  buildLibraryDocumentSubTypeSidebarLabels,
  getDocumentOverrideKey,
} from "./utils";

function applyIncludePreliminaryStatementToPreferences(
  prefs: PreliminaryStatementPreferencesWithDocument | undefined,
  value: boolean | null,
  documentOverrideKey: string | undefined,
): PreliminaryStatementPreferencesWithDocument {
  const docPrefs = prefs?.documentPreferences;
  if (documentOverrideKey) {
    const existingOverride = docPrefs?.documentOverrides?.[documentOverrideKey];
    const nextOverrides = {
      ...docPrefs?.documentOverrides,
      [documentOverrideKey]: {
        ...existingOverride,
        responseOptions: {
          ...existingOverride?.responseOptions,
          includePreliminaryStatement: value,
        },
      },
    };
    return {
      ...prefs,
      documentPreferences: {
        ...docPrefs,
        documentOverrides: nextOverrides,
      },
    };
  }
  return {
    ...prefs,
    documentPreferences: {
      ...docPrefs,
      responseOptions: {
        ...docPrefs?.responseOptions,
        includePreliminaryStatement: value,
      },
    },
  };
}

interface Props {
  preliminaryStatements: MiscContent[];
  onUpdate: LibraryMiscContentOnUpdate;
}

export default function PreliminaryStatement({
  preliminaryStatements,
  onUpdate,
}: Props) {
  const [content, setContent] = useState("");
  const { user, firm, setFirm, updatePreferences, defaultJurisdiction } =
    useAuth();
  const firmApi = useFirmApi();
  const [isSharedWithFirm, setIsSharedWithFirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);
  const [nextSubType, setNextSubType] = useState<DocumentSubType | undefined>();
  const [toastMessage, setToastMessage] = useState("");
  const [selectedStatement, setSelectedStatement] =
    useState<MiscContent | null>();
  const [selectedStatementSubType, setSelectedStatementSubType] = useState<
    DocumentSubType | undefined
  >();
  const [includeInEveryOutput, setIncludeInEveryOutput] = useState(false);

  const canSetFirmStandard = user?.role?.includes(Role.FirmAdmin);

  const documentOverrideKey = getDocumentOverrideKey(selectedStatementSubType);

  const savedIncludePreliminaryStatement = useMemo(() => {
    const prefs = isSharedWithFirm ? firm?.preferences : user?.preferences;
    const docPrefs = prefs?.documentPreferences;
    const baseInclude =
      docPrefs?.responseOptions?.includePreliminaryStatement ?? true;
    if (
      documentOverrideKey &&
      docPrefs?.documentOverrides?.[documentOverrideKey]
    ) {
      const overrideInclude =
        docPrefs.documentOverrides[documentOverrideKey].responseOptions
          ?.includePreliminaryStatement;
      return overrideInclude !== undefined && overrideInclude !== null
        ? overrideInclude
        : baseInclude;
    }
    return baseInclude;
  }, [
    isSharedWithFirm,
    documentOverrideKey,
    firm?.preferences,
    user?.preferences,
  ]);

  const subTypes = buildLibraryDocumentSubTypeSidebarLabels(
    defaultJurisdiction?.id ?? "",
    { includeFormInterrogatories: true },
  );

  useEffect(() => {
    // Initialize with default statement if no subtype is selected
    if (!selectedStatementSubType && !selectedStatement) {
      const defaultStatement = preliminaryStatements.find(
        (x) => !x.documentSubType,
      );
      if (defaultStatement) {
        setSelectedStatement(defaultStatement);
        setContent(defaultStatement.content || "");
        setIsSharedWithFirm(!!defaultStatement.sharedWithFirmId);
        setIsDirty(false);
      }
    }
  }, [preliminaryStatements, selectedStatementSubType, selectedStatement]);

  useEffect(() => {
    // Update content when selectedStatement changes (but not during initial load)
    if (selectedStatement?.content !== undefined) {
      setContent(selectedStatement.content || "");
      setIsSharedWithFirm(!!selectedStatement.sharedWithFirmId);
      setIsDirty(false);
    }
  }, [selectedStatement]);

  useEffect(() => {
    // load includePreliminaryStatement from firm or user preferences (default true when null)
    if (selectedStatement) {
      setIncludeInEveryOutput(savedIncludePreliminaryStatement);
    }
  }, [selectedStatement, savedIncludePreliminaryStatement]);

  function handleChangeContent(val: string) {
    if (val !== content) {
      setContent(val);
      checkIsDirty();
    }
  }

  async function onSave() {
    if (selectedStatement) {
      const update = {
        ...selectedStatement,
        content: content,
        sharedWithFirmId: isSharedWithFirm ? firm?.id : undefined,
      };
      const result = await onUpdate(update);
      if (isSharedWithFirm && firm?.id) {
        const nextFirmPrefs = applyIncludePreliminaryStatementToPreferences(
          firm.preferences,
          includeInEveryOutput,
          documentOverrideKey,
        );
        await firmApi.firmUpdatePreferences({
          id: firm.id,
          firmPreferences: nextFirmPrefs,
        });
        setFirm((prev) =>
          prev ? { ...prev, preferences: nextFirmPrefs } : prev,
        );
        // when saving firm standard, clear user override so they inherit firm setting
        if (user) {
          const nextUserPrefs = applyIncludePreliminaryStatementToPreferences(
            user.preferences,
            null,
            documentOverrideKey,
          );
          await updatePreferences(nextUserPrefs);
        }
      } else if (user) {
        const nextUserPrefs = applyIncludePreliminaryStatementToPreferences(
          user.preferences,
          includeInEveryOutput,
          documentOverrideKey,
        );
        await updatePreferences(nextUserPrefs);
      }
      setToastMessage("");
      setToastMessage("Preliminary Statement Saved!");
      setIsDirty(false);
      setSelectedStatement(result);
    }
  }

  function handleChangeSelectedSubType(subType: DocumentSubType | undefined) {
    if (!isDirty) {
      handleSetSelectedStatementSubType(subType);
    } else {
      setNextSubType(subType);
      setShowDirtyConfirm(true);
    }
  }

  function handleSetSelectedStatementSubType(
    subType: DocumentSubType | undefined,
  ) {
    const next = subType ?? nextSubType;
    const statement = preliminaryStatements.find(
      (x) => x.documentSubType === next,
    );
    setSelectedStatement(statement);
    setSelectedStatementSubType(next);
    setShowDirtyConfirm(false);
    setNextSubType(undefined);
    setIsDirty(false);
  }

  function handleCreateOverrideClick() {
    const defaultText = preliminaryStatements.find((x) => !x.documentSubType);

    setSelectedStatement({
      content: defaultText?.content,
      documentType: defaultText?.documentType,
      type: MiscContentType.PreliminaryStatement,
      documentSubType: selectedStatementSubType,
    });
  }

  function handleChangeCancel() {
    setContent(selectedStatement?.content || "");
    setIsSharedWithFirm(!!selectedStatement?.sharedWithFirmId);
    setIncludeInEveryOutput(savedIncludePreliminaryStatement);
    setIsDirty(false);
  }

  function handleFirmStandardClick(checked: boolean) {
    //set isn't instant so compare checked to the current value of selectedStatement?.sharedWithFirmId here instead
    const dirty =
      content !== selectedStatement?.content ||
      checked !== !!selectedStatement?.sharedWithFirmId ||
      includeInEveryOutput !== savedIncludePreliminaryStatement;
    setIsDirty(dirty);
    setIsSharedWithFirm(checked);
  }

  function handleIncludeInEveryOutputChange(checked: boolean) {
    setIncludeInEveryOutput(checked);
    setIsDirty(
      content !== selectedStatement?.content ||
        isSharedWithFirm !== !!selectedStatement?.sharedWithFirmId ||
        checked !== savedIncludePreliminaryStatement,
    );
  }

  function checkIsDirty() {
    const dirty =
      content !== selectedStatement?.content ||
      isSharedWithFirm !== !!selectedStatement?.sharedWithFirmId ||
      includeInEveryOutput !== savedIncludePreliminaryStatement;
    setIsDirty(dirty);
  }

  return (
    <>
      <LeavingGuard
        when={isDirty}
        message="You have unsaved changes, continue and lose them?"
        title="Unsaved changes!"
      />
      <div
        className={`${styles.stepContainer} ${styles.preliminaryStatementContainer}`}
        style={{ gap: 0 }}
      >
        <ToastContainer
          className={styles.preliminaryStatementToastContainer}
          position="top-end"
        >
          <Toast
            onClose={() => setToastMessage("")}
            show={!!toastMessage}
            delay={2500}
            bg="success"
            autohide
          >
            <Toast.Body>
              <div className={styles.preliminaryStatementToastBody}>
                <div>{toastMessage}</div>
                <CloseButton
                  className={styles.preliminaryStatementToastClose}
                  onClick={() => setToastMessage("")}
                />
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <div className={styles.preliminaryStatementSidebar}>
          {
            // biome-ignore lint/a11y/useKeyWithClickEvents: replace with button
            <div
              key={"default"}
              onClick={() => {
                handleChangeSelectedSubType(undefined);
              }}
              className={clsx(styles.libraryObjection, {
                [styles.libraryObjectionSelected]: !selectedStatementSubType,
              })}
            >
              Default
            </div>
          }
          {Object.keys(DocumentSubType).map((o) => {
            const i = Number(o);
            const t = subTypes[i as keyof typeof subTypes];

            return (
              t && (
                // biome-ignore lint/a11y/useKeyWithClickEvents: replace with button
                <div
                  key={o}
                  onClick={() => {
                    handleChangeSelectedSubType(i);
                  }}
                  className={clsx(styles.libraryObjection, {
                    [styles.libraryObjectionSelected]:
                      i === selectedStatementSubType,
                  })}
                >
                  {t}
                </div>
              )
            );
          })}
        </div>
        <div className={styles.preliminaryStatementContent}>
          {selectedStatement ? (
            <>
              {!canSetFirmStandard && !!selectedStatement.sharedWithFirmId && (
                <Alert variant="info">
                  {
                    "This Preliminary Statement is set as your Firm's Standard, contact your Firm Admin if you need to make changes."
                  }
                </Alert>
              )}

              <Form.Group className={styles.preliminaryStatementFormGroup}>
                <Form.Check
                  id={"includeInEveryOutput"}
                  type={"checkbox"}
                  label={
                    "Include Preliminary Statements in every output document"
                  }
                  checked={includeInEveryOutput}
                  onChange={(e) =>
                    handleIncludeInEveryOutputChange(e.target.checked)
                  }
                  className={styles.preliminaryStatementIncludeCheckbox}
                />
                <Form.Label>Content</Form.Label>
                <CustomEditor
                  handleChange={handleChangeContent}
                  editorContent={content}
                  showAdvancedOptions={true}
                  showMenu={true}
                  showHighlight={true}
                  isDisabled={
                    !canSetFirmStandard && !!selectedStatement.sharedWithFirmId
                  }
                  isFullWidth={true}
                  isFullHeight={true}
                />
                {canSetFirmStandard && (
                  <Form.Check
                    id={"sharedwithfirm"}
                    type={"checkbox"}
                    label="Use as Firm Standard"
                    checked={isSharedWithFirm}
                    onChange={(e) => handleFirmStandardClick(e.target.checked)}
                  />
                )}
                <FormModal
                  title={"Abandon changes?"}
                  cancelText="Cancel"
                  confirmText="Yes, abandon"
                  show={showDirtyConfirm}
                  onClose={() => setShowDirtyConfirm(false)}
                  onConfirm={() =>
                    handleSetSelectedStatementSubType(nextSubType)
                  }
                >
                  <div>
                    Are you sure you want to abandon your saved changes?
                  </div>
                </FormModal>
              </Form.Group>
              <PlaceholderTutorialLink />
              <div className={styles.preliminaryStatementButtonGroup}>
                <Button
                  variant="default"
                  onClick={onSave}
                  disabled={!isDirty && !!selectedStatement.id}
                >
                  Save
                </Button>
                <Button
                  variant="link"
                  onClick={handleChangeCancel}
                  disabled={!isDirty && !!selectedStatement.id}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p>
                This Document Type will use the Default Preliminary Statement.
              </p>
              <Button onClick={handleCreateOverrideClick}>Create New</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
